'use client';

import { useEffect } from 'react';
import { ProgressBar } from '@/app_components/ui/ProgressBar';
import { registerExperimentVariants } from '@/app_lib/devdash/utils/experimentVariantsRegistry';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';
import type { VariantProps } from '@/main/lib/providers/OptimizelyProvider';

const VARIANT_FLAG = 'readingProgressBarVariant';

const variants = {
  bodywidth: (
    <ProgressBar
      bodyWidth
      key='bodywidth'
    />
  ),
  control: <ProgressBar key='control' />,
  delayed10: (
    <ProgressBar
      appearAfter={10}
      key='delayed10'
    />
  ),
  delayed33: (
    <ProgressBar
      appearAfter={33}
      key='delayed33'
    />
  ),
  mobileonly: (
    <ProgressBar
      key='mobileonly'
      mobileOnly
    />
  ),
} as const;

const DEFAULT_VARIANT: keyof typeof variants = 'control';

/**
 * Always renders a reading progress bar (SSR + first paint = default bar).
 * When Optimizely loads, the readingProgressBarVariant test may switch to a variant.
 * This avoids CLS from "no bar" → "bar" because we never wait for Optimizely to show the bar.
 */
// biome-ignore lint/style/useNamingConvention: React component
export function ReadingProgressBar() {
  const { userFlags, optimizely } = useOptimizely();

  useEffect(() => {
    registerExperimentVariants(VARIANT_FLAG, Object.keys(variants));
  }, []);

  // Always render a progress bar. Before Optimizely is ready or when not in test, use default.
  const flag = userFlags[VARIANT_FLAG] as VariantProps | undefined;
  const variantKey = optimizely && flag?.variant ? (flag.variant?.toLowerCase() as keyof typeof variants) : null;
  const resolved =
    (variantKey && variantKey in variants ? variants[variantKey] : variants[DEFAULT_VARIANT]) ??
    variants[DEFAULT_VARIANT];

  return <>{resolved}</>;
}
